/**
 * FHS Real-Time Pricing Manager
 *
 * Handles fetching and updating item prices from FareHarbor API.
 * Features:
 * - Request batching: Multiple blocks requesting prices are batched into one API call
 * - Deduplication: Same item post requested by multiple blocks only fetches once
 * - Generic DOM updates: Uses data attributes for flexible block support
 */

import {
  computeCompareAtCents,
  parseLowPriceCents,
  shouldDisplayCompareAt,
} from '../../../blocks/utils/js/strikethroughPricing.js';

// Create global namespace if it doesn't exist
window.FHS = window.FHS || {};

const CURRENCY_LOCALES = window.FHS?.currencyLocales || {};
const CURRENCY_SYMBOLS = window.FHS?.currencySymbols || {};

/**
 * Get the display symbol for a currency code.
 *
 * @param {string} code - ISO currency code (case-insensitive).
 * @returns {string} Symbol such as '$', '€', '£'.
 */
function getCurrencySymbol(code) {
  if (!code) return '$';
  return CURRENCY_SYMBOLS[code.toLowerCase()] || code.toUpperCase() + ' ';
}

/**
 * Format price from cents to display string.
 *
 * Uses DECIMAL number formatting with the currency's canonical locale
 * for separators, then manually prepends the symbol. This matches the
 * server-side Currency::formatPrice() output so real-time updates don't
 * shift symbol placement (e.g. EUR always renders as "€1.650").
 *
 * @param {number|string} priceInCents - Price in cents
 * @param {string} currencyCode - Currency code (e.g., 'USD')
 * @returns {string} Formatted price string
 */
/**
 * Read compare-at config from server-rendered data attributes.
 *
 * @param {HTMLElement} block - Element with data-strikethrough-* on self or ancestor.
 * @returns {{ mode: string, fixed_cents: number, percent: number, absolute_cents?: number }|null}
 */
function getStrikethroughConfigFromDataset(block) {
  if (!block) {
    return null;
  }

  let el = block;
  for (let i = 0; i < 6 && el; i++) {
    const mode = el.dataset?.strikethroughMode;

    if (mode === 'fixed_cents') {
      const fixedCents = parseInt(el.dataset.strikethroughFixedCents, 10);
      return !isNaN(fixedCents) && fixedCents > 0
        ? { mode, fixed_cents: fixedCents, percent: 0 }
        : null;
    }

    if (mode === 'percent') {
      const percent = parseFloat(el.dataset.strikethroughPercent);
      return !isNaN(percent) && percent > 0
        ? { mode, fixed_cents: 0, percent }
        : null;
    }

    if (mode === 'absolute_cents') {
      const absoluteCents = parseInt(el.dataset.strikethroughAbsoluteCents, 10);
      return !isNaN(absoluteCents) && absoluteCents > 0
        ? { mode, fixed_cents: 0, percent: 0, absolute_cents: absoluteCents }
        : null;
    }

    el = el.parentElement;
  }

  return null;
}

function formatPrice(priceInCents, currencyCode = 'USD') {
  const { __ } = wp.i18n;
  if (priceInCents === null || priceInCents === undefined) return '';

  if (
    typeof priceInCents === 'string' &&
    priceInCents.toLowerCase() === 'free'
  ) {
    return __('Free', 'fareharbor-sites');
  }

  const cents = parseInt(priceInCents, 10);
  if (isNaN(cents)) return '';

  if (cents === 0) return __('Free', 'fareharbor-sites');

  const dollars = cents / 100;
  const locale = CURRENCY_LOCALES[currencyCode.toLowerCase()] || 'en-US';
  const decimals = dollars % 1 === 0 ? 0 : 2;
  const symbol = getCurrencySymbol(currencyCode).trimEnd();

  try {
    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: 2,
    }).format(dollars);

    return symbol + formatted;
  } catch (e) {
    return `${symbol}${dollars.toFixed(decimals)}`;
  }
}

/**
 * Format price from cents to HTML with separate symbol and amount spans.
 *
 * @param {number|string} priceInCents - Price in cents
 * @param {string} currencyCode - Currency code (e.g., 'USD')
 * @returns {string} Formatted price HTML
 */
function formatPriceHtml(priceInCents, currencyCode = 'USD') {
  if (priceInCents === null || priceInCents === undefined) return '';

  const { __ } = wp.i18n;

  if (
    typeof priceInCents === 'string' &&
    priceInCents.toLowerCase() === 'free'
  ) {
    return escapeHtml(__('Free', 'fareharbor-sites'));
  }

  const cents = parseInt(priceInCents, 10);
  if (isNaN(cents)) return '';

  if (cents === 0) return escapeHtml(__('Free', 'fareharbor-sites'));

  const dollars = cents / 100;
  const locale = CURRENCY_LOCALES[currencyCode.toLowerCase()] || 'en-US';
  const decimals = dollars % 1 === 0 ? 0 : 2;
  const symbol = getCurrencySymbol(currencyCode).trimEnd();

  let formatted;
  try {
    formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: 2,
    }).format(dollars);
  } catch (e) {
    formatted = dollars.toFixed(decimals);
  }

  return `<span class="fhs-price-symbol">${escapeHtml(symbol)}</span><span class="fhs-price-amount">${escapeHtml(formatted)}</span>`;
}

/**
 * Escape HTML entities to prevent XSS
 *
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function getCustomerTypeComparePrices(block) {
  const raw = block?.dataset?.customerTypeComparePrices;
  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function getCustomerTypeComparePriceKeys(block) {
  const raw = block?.dataset?.customerTypeComparePriceKeys;
  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function getCustomerTypeCompareKey(type) {
  // TODO: Remove this fallback when the FareHarbor price-preview endpoint returns customer prototype ids.
  const normalizeText = value =>
    String(value || '')
      .replace(/<[^>]*>/g, '')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim()
      .replace(/\s+/g, ' ');

  const normalizeLabel = value => {
    const parts = normalizeText(value).split(' ').filter(Boolean);
    const lastIndex = parts.length - 1;
    const last = parts[lastIndex] || '';

    if (
      last.length > 3 &&
      last.endsWith('s') &&
      !last.endsWith('ss') &&
      !last.endsWith('us')
    ) {
      parts[lastIndex] = last.slice(0, -1);
    }

    return parts.join(' ');
  };

  return `${normalizeLabel(type?.label)}|${normalizeText(type?.note)}`;
}

function renderCustomerTypePriceHtml(price, currency, comparePrices, comparePriceKeys, type) {
  const formattedPrice = formatPriceHtml(price, currency);
  const priceCents = parseInt(price, 10);
  const directCompare = comparePrices?.[type?.pk];
  const keyCompare = comparePriceKeys?.[getCustomerTypeCompareKey(type)];
  const compareCents = parseInt(directCompare ?? keyCompare, 10);

  if (
    Number.isFinite(priceCents) &&
    Number.isFinite(compareCents) &&
    compareCents > 0 &&
    compareCents !== priceCents
  ) {
    return `<del class="item-pricing-block__compare">${formatPriceHtml(compareCents, currency)}</del><span class="item-pricing-block__current">${formattedPrice}</span>`;
  }

  return formattedPrice;
}

function normalizeCustomerTypeEntry(key, type, isList) {
  const pk =
    type?.pk ??
    type?.id ??
    (!isList && /^\d+$/.test(String(key)) ? key : '');

  return {
    pk: String(pk || ''),
    label: type?.label ?? type?.singular ?? type?.plural ?? '',
    note: type?.note ?? '',
    price: type?.price ?? 0,
  };
}

/**
 * Update all pricing elements for an item post using data attributes
 *
 * Blocks use these data attributes:
 * - data-item-post-id="123" (WordPress post ID of the item)
 * - data-price-field="low_price" (for from/low price)
 * - data-price-field="customer_type_price" + data-customer-type-pk="456" (for customer type prices)
 *
 * @param {string|number} itemPostId - WordPress post ID of the item
 * @param {Object} pricing - Pricing data from API
 */
function updateItemPostPricing(itemPostId, pricing) {
  const pricingBlocks = document.querySelectorAll(
    `[data-item-post-id="${itemPostId}"]`,
  );

  pricingBlocks.forEach(block => {
    // Check if server already rendered cached pricing
    const isCachedRender = block.dataset.cachedRender === 'true';

    // Skip if already rendered with cached pricing
    if (isCachedRender) {
      return;
    }

    // Update low_price fields
    block.querySelectorAll('[data-price-field="low_price"]').forEach(el => {
      const currentPrice = el.textContent.trim();
      const hasPriceMarkup =
        el.querySelector('.fhs-price-symbol') &&
        el.querySelector('.fhs-price-amount');
      const newPrice = formatPrice(pricing.low_price, pricing.currency);
      const newPriceHtml = formatPriceHtml(pricing.low_price, pricing.currency);

      // Update when the price changed or when we still need to upgrade plain text to markup.
      if (currentPrice !== newPrice || !hasPriceMarkup) {
        el.innerHTML = newPriceHtml;
        el.classList.add('fhs-price-updated');
      }
    });

    const compareEls = block.querySelectorAll('[data-price-field="compare_at_price"]');
    if (compareEls.length && pricing.low_price != null) {
      const baseCents = parseLowPriceCents(pricing.low_price);
      const strikethroughConfig = getStrikethroughConfigFromDataset(block);
      const compareCents =
        baseCents != null && strikethroughConfig
          ? computeCompareAtCents(baseCents, strikethroughConfig)
          : null;

      if (shouldDisplayCompareAt(baseCents, compareCents, strikethroughConfig)) {
        const compareStr = formatPrice(compareCents, pricing.currency);
        compareEls.forEach(cel => {
          cel.textContent = compareStr;
          cel.classList.add('fhs-price-updated');
        });
      }
    }

    // Update high_price fields (for range display)
    block.querySelectorAll('[data-price-field="high_price"]').forEach(el => {
      const currentPrice = el.textContent.trim();
      const hasPriceMarkup =
        el.querySelector('.fhs-price-symbol') &&
        el.querySelector('.fhs-price-amount');
      const newPrice = formatPrice(pricing.high_price, pricing.currency);
      const newPriceHtml = formatPriceHtml(pricing.high_price, pricing.currency);

      // Update when the price changed or when we still need to upgrade plain text to markup.
      if (currentPrice !== newPrice || !hasPriceMarkup) {
        el.innerHTML = newPriceHtml;
        el.classList.add('fhs-price-updated');
      }
    });

    // Replace entire customer types table with real-time data
    if (pricing.customer_types) {
      const customerTypeKeys = Object.keys(pricing.customer_types);
      const isCustomerTypeList = Array.isArray(pricing.customer_types);
      const comparePrices = getCustomerTypeComparePrices(block);
      const comparePriceKeys = getCustomerTypeComparePriceKeys(block);

      // Find the pricing table tbody
      const tbody = block.querySelector('.item-pricing-block__table tbody');

      if (tbody) {
        // Build new rows from API data (match PHP render.php structure exactly)
        const newRows = customerTypeKeys
          .map(pk => {
            const type = normalizeCustomerTypeEntry(
              pk,
              pricing.customer_types[pk],
              isCustomerTypeList,
            );
            const { __ } = wp.i18n;
            const typeLabel = type.label || __('N/A', 'fareharbor-sites');
            const typeNote = type.note || '';
            const formattedPrice = renderCustomerTypePriceHtml(
              type.price,
              pricing.currency,
              comparePrices,
              comparePriceKeys,
              type,
            );

            // Match PHP: only include details div if note exists AND is not empty/whitespace
            const noteHtml =
              typeNote && typeNote.trim() !== ''
                ? `
                        <div class="item-pricing-block__details">
                            ${escapeHtml(typeNote)}
                        </div>`
                : '';

            return `
                <tr class="item-pricing-block__row">
                    <td class="item-pricing-block__cell item-pricing-block__cell--type">
                        <div class="item-pricing-block__label">
                            ${escapeHtml(typeLabel)}
                        </div>${noteHtml}
                    </td>
                    <td class="item-pricing-block__cell item-pricing-block__cell--price"
                        data-price-field="customer_type_price"
                        data-customer-type-pk="${escapeHtml(type.pk)}">
                        ${formattedPrice}
                    </td>
                </tr>`;
          })
          .join('\n');

        // Only update DOM if content actually changed (prevents unnecessary reflows)
        // Normalize whitespace for comparison to handle formatting differences
        const currentHTML = tbody.innerHTML
          .trim()
          .replace(/\s+/g, ' ')
          .replace(/>\s+</g, '><');
        const newHTML = newRows
          .trim()
          .replace(/\s+/g, ' ')
          .replace(/>\s+</g, '><');

        if (currentHTML !== newHTML) {
          tbody.innerHTML = newRows;
        }
      }
    }
  });
}

/**
 * Pricing Request Manager
 * Handles batching and deduplication of pricing requests.
 */
class PricingRequestManager {
  constructor() {
    this.pendingRequests = new Map(); // itemPostId -> { data, callbacks }
    this.debounceTimer = null;
    this.batchDelay = 100; // ms to wait for more requests
    this.maxBatchSize = 25; // Maximum item posts per API request
  }

  /**
   * Request pricing for an item post. Returns a promise that resolves with pricing data.
   * Multiple requests for the same post are batched and deduplicated.
   *
   * @param {string|number} itemPostId - WordPress post ID of the item
   * @param {Object} itemPricingData - FareHarbor data (item_id/item_ids, shortname, sheet, language)
   * @returns {Promise<Object>} Pricing data
   */
  requestPricing(itemPostId, itemPricingData) {
    return new Promise((resolve, reject) => {
      // Add to pending requests
      if (!this.pendingRequests.has(itemPostId)) {
        this.pendingRequests.set(itemPostId, {
          data: itemPricingData,
          callbacks: [],
        });
      }

      this.pendingRequests.get(itemPostId).callbacks.push({ resolve, reject });

      // Debounce batch execution
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(
        () => this.executeBatch(),
        this.batchDelay,
      );
    });
  }

  /**
   * Execute the batched pricing request.
   * Automatically splits large batches into multiple requests.
   */
  async executeBatch() {
    const requests = Array.from(this.pendingRequests.entries());
    if (requests.length === 0) return;

    // Clear pending
    const batch = new Map(this.pendingRequests);
    this.pendingRequests.clear();

    // Split into chunks if batch is too large
    const chunks = [];
    for (let i = 0; i < requests.length; i += this.maxBatchSize) {
      chunks.push(requests.slice(i, i + this.maxBatchSize));
    }

    try {
      // Process all chunks in parallel
      const chunkPromises = chunks.map(async chunk => {
        const batchRows = chunk.map(([id, { data }]) => ({
          item_post_id: id,
          ...data,
        }));

        const response = await fetch('/wp-json/fhs/v1/batch-pricing', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: batchRows }),
        });

        if (!response.ok) {
          throw new Error(`API returned ${response.status}`);
        }

        return await response.json();
      });

      // Wait for all chunks to complete
      const allResults = await Promise.all(chunkPromises);

      // Merge results from all chunks
      const results = Object.assign({}, ...allResults);

      // Update DOM and resolve callbacks for each item post
      for (const [itemPostId, { callbacks }] of batch.entries()) {
        const pricing = results[itemPostId];

        if (pricing) {
          // Update DOM using data attributes
          updateItemPostPricing(itemPostId, pricing);
        }

        callbacks.forEach(cb => cb.resolve(pricing));
      }
    } catch (error) {
      // Reject all callbacks
      for (const [, { callbacks }] of batch.entries()) {
        callbacks.forEach(cb => cb.reject(error));
      }
    }
  }
}

// Create global singleton instance
window.FHS.pricingManager = new PricingRequestManager();

/**
 * Auto-initialize on DOM ready.
 * Finds all pricing blocks and requests updates if real-time pricing is enabled.
 */
function initRealtimePricing() {
  // Check if real-time pricing is enabled
  if (!window.FHS?.realtimePricingEnabled) {
    return;
  }

  // Find all pricing blocks that opt-in to real-time pricing
  const pricingBlocks = document.querySelectorAll(
    '[data-item-post-id][data-realtime-pricing="true"]',
  );

  if (pricingBlocks.length === 0) {
    return;
  }

  pricingBlocks.forEach(block => {
    const itemPostId = block.dataset.itemPostId;
    const itemIds = (block.dataset.itemIds || '')
      .split(',')
      .map(id => id.trim())
      .filter(Boolean);
    const itemPricingData = {
      item_id: block.dataset.itemId,
      item_ids: itemIds,
      shortname: block.dataset.shortname,
      sheet: block.dataset.sheet || '',
      language: block.dataset.language || '',
      environment: block.dataset.environment || 'production',
    };

    // Skip if missing required data
    if (
      !itemPostId ||
      (!itemPricingData.item_id && itemPricingData.item_ids.length === 0) ||
      !itemPricingData.shortname
    ) {
      return;
    }

    // Request pricing (auto-batched and deduplicated)
    window.FHS.pricingManager.requestPricing(itemPostId, itemPricingData);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRealtimePricing);
} else {
  initRealtimePricing();
}
